import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { authApi } from '@/services/authApi';
import { profileApi, ProfileApiResponse } from '@/services/profileApi';
import { apiClient } from '@/services/api';
import {
  User,
  LoginRequest,
  RegisterRequest,
  AuthContextType,
  UserApiResponse,
} from '@/types';

const mapApiUserToUser = (
  apiUser: UserApiResponse | ProfileApiResponse
): User => {
  if ('handle' in apiUser && 'personal' in apiUser) {
    const profileUser = apiUser as ProfileApiResponse;
    return {
      id: String(profileUser.id),
      handle: profileUser.handle,
      avatar: profileUser.avatar || undefined,
      personal: {
        firstName: profileUser.personal.firstName,
        lastName: profileUser.personal.lastName,
        middleName: profileUser.personal.middleName,
        bio: profileUser.personal.bio,
        dateOfBirth: profileUser.personal.dateOfBirth || undefined,
        gender: (profileUser.personal.gender as any) || 'not_selected',
      },
      contact: {
        email: profileUser.security.email,
        phone: undefined,
        socials: profileUser.socials || [],
      },
      location: {
        label: '',
        birthPlace: profileUser.location.birthPlace,
        residencePlace: profileUser.location.residencePlace,
      },
      metrics: profileUser.metrics,
      preferences: {
        language: 'ru',
        avatarVisibility:
          (profileUser.preferences.avatarVisibility as any) || 'everyone',
        emailNotificationsEnabled: Boolean(
          (profileUser as any).preferences?.emailNotificationsEnabled
        ),
      },
      security: {
        password: profileUser.security.password,
        hasSubscription: profileUser.security.hasSubscription,
        lastLogin: undefined,
      },
      wallet: profileUser.wallet,
      createdAt: profileUser.createdAt,
      updatedAt: profileUser.updatedAt,
    };
  }

  const simpleUser = apiUser as UserApiResponse;
  const displayName = simpleUser.name || simpleUser.username || '';
  const [firstName = '', lastName = ''] = displayName
    ? displayName.split(' ')
    : [''];

  const userId = String(simpleUser.id);

  return {
    id: userId,
    handle: simpleUser.username || userId,
    avatar: simpleUser.avatar,
    personal: {
      firstName,
      lastName,
      middleName: '',
      bio: '',
      dateOfBirth: undefined,
      gender: 'not_selected',
    },
    contact: {
      email: simpleUser.email,
      phone: undefined,
      socials: [],
    },
    location: {
      label: '',
      birthPlace: {},
      residencePlace: {},
    },
    metrics: {
      followers: 0,
      following: 0,
      coauthors: 0,
    },
    preferences: {
      language: 'ru',
      avatarVisibility: 'everyone',
      emailNotificationsEnabled: false,
    },
    security: {
      password: '',
      hasSubscription: false,
      lastLogin: undefined,
    },
    wallet: simpleUser.wallet,
    createdAt: simpleUser.createdAt,
    updatedAt: simpleUser.updatedAt,
  };
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
          apiClient.setTokens(
            accessToken,
            localStorage.getItem('refresh_token')
          );
          try {
            const profileResponse = await profileApi.getProfile();
            if (profileResponse.success) {
              setUser(mapApiUserToUser(profileResponse.data));
            }
          } catch (profileError) {
            const authResponse = await authApi.getCurrentUser();
            if (authResponse.success) {
              setUser(mapApiUserToUser(authResponse.data));
            }
          }
        }
      } catch (error: any) {
        setUser(null);
        if (error?.status === 401) {
          apiClient.setTokens(null, null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth_token');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (
    credentials: LoginRequest
  ): Promise<{
    success: boolean;
    error?: string;
    emailNotVerified?: boolean;
    email?: string;
  }> => {
    setIsLoading(true);

    try {
      const response = await authApi.login(credentials);

      if (response.success && response.data.access && response.data.refresh) {
        try {
          const profileResponse = await profileApi.getProfile();
          if (profileResponse.success) {
            setUser(mapApiUserToUser(profileResponse.data));
            return { success: true };
          }
        } catch (profileError) {
          const userResponse = await authApi.getCurrentUser();
          if (userResponse.success) {
            setUser(mapApiUserToUser(userResponse.data));
            return { success: true };
          }
        }
      }

      return { success: false, error: response.message || 'Ошибка входа' };
    } catch (error: any) {
      if (
        error.status === 403 ||
        error.code === 'email_not_verified' ||
        error.data?.code === 'email_not_verified'
      ) {
        return {
          success: false,
          emailNotVerified: true,
          email: credentials.email,
          error: error.message,
        };
      }

      return {
        success: false,
        error: error.message || 'Произошла ошибка при входе',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    userData: RegisterRequest
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    try {
      const registerResponse = await authApi.register(userData);

      if (registerResponse.success) {
        return { success: true };
      }

      return {
        success: false,
        error: registerResponse.message || 'Ошибка регистрации',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Произошла ошибка при регистрации',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithProvider = async (
    provider: string
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    try {
      const response = await authApi.signInWithProvider(provider);

      if (response.success && response.data.user) {
        setUser(mapApiUserToUser(response.data.user));
        localStorage.setItem('auth_token', response.data.token);
        return { success: true };
      }

      return {
        success: false,
        error: response.message || 'Ошибка входа через социальную сеть',
      };
    } catch (error: any) {
      return {
        success: false,
        error:
          error.message || 'Произошла ошибка при входе через социальную сеть',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setIsLoading(true);

    try {
      await authApi.logout();

      setUser(null);
      apiClient.setTokens(null, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_token');
    } catch (error) {
      setUser(null);
      apiClient.setTokens(null, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_token');
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentUser = async (): Promise<User | null> => {
    try {
      try {
        const profileResponse = await profileApi.getProfile();
        if (profileResponse.success) {
          const mapped = mapApiUserToUser(profileResponse.data);
          setUser(mapped);
          return mapped;
        }
      } catch (profileError) {
        const authResponse = await authApi.getCurrentUser();
        if (authResponse.success) {
          const mapped = mapApiUserToUser(authResponse.data);
          setUser(mapped);
          return mapped;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    signInWithProvider,
    signOut,
    getCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
